'use client';
import React, { FC, useState } from 'react';
import { Entry } from 'contentful';
import { Document } from '@contentful/rich-text-types';
import {
  TypeDecorativeDividerFields,
  TypeImage,
  TypeNewsletterBlockFields,
  TypeProductPage,
  TypeTextBlock,
} from '@/core/generated-types';

import useFeature from '@/hooks/useFeature';
import {
  AccordionSection,
  CardsRow,
  DecorativeDivider,
  DynamicComponent,
  HeaderText,
  NewsletterBlock,
  ProductDetailsHeader,
  ProductSubFAQ,
  RichText,
  NotFound,
} from '@/components';
import { ProductContextProvider } from '@/context/ProductContext';
import { ShopifyProduct } from '@/lib/shopify';
import { YotpoReviewsWidget, TrustedShopReviews } from '@/components';
import { BackToTop } from '@/components/common/BackToTop';
import { Seo } from '@/components/ui/Seo';
import { HeaderSize } from '@/components/ui/HeaderText';
import {
  ExtendedProductFields,
  buildCanonicalUrl,
} from '@/core/contentfulConfig';
import {
  InventoryStatusCopy,
  ProductVariantCopy,
  ProductPageGeneralCopy,
} from '@/lib/contentful';
import ProductSchema from '@/components/common/Schema/ProductSchema';
import {
  shouldUseYotpoReviews,
  shouldUseTrustedShopsReviews,
} from '@/utils/helpers';
import { getProductPageTranslation } from '@/utils/getProductPageTranslation';
import { ReviewTotals } from '@/lib/yotpo';
import ReviewsSchema from '@/components/common/Schema/ReviewsSchema';

export interface ProductPageLayoutProps extends TypeProductPage {
  shopifyProduct: ShopifyProduct | null;
  shopifyProductAddOns: { [title: string]: ShopifyProduct | null };
  shopifyAdditionalProducts: ShopifyProduct[];
  inventoryStatusCopy: InventoryStatusCopy | null;
  productVariantsCopy: ProductVariantCopy | null;
  productPageGeneralCopy: ProductPageGeneralCopy | null;
  yotpoReviews?: ReviewTotals;
}

export const ProductPageLayout: FC<ProductPageLayoutProps> = ({
  shopifyProduct,
  shopifyProductAddOns,
  shopifyAdditionalProducts,
  inventoryStatusCopy,
  productVariantsCopy,
  productPageGeneralCopy,
  yotpoReviews,
  ...page
}) => {
  const [carouselImagePosition, setCarouselImagePosition] = useState(null);
  const {
    fields: {
      product,
      additionalProducts,
      productDescription,
      lookInside,
      marketingComponents,
      productFaq,
      productSubFaq,
      enableYotpoReviewsSummary,
      enableYotpoReviewsWidget,
      enableTrustedShopsReviewsSummary,
      enableTrustedShopsReviewsWidget,
      newsletterSignup,
      lookInsideDivider,
      relatedProducts,
      lookInsideLeadIn,
      featureFlagName,
    },
  } = page;
  const metadataFields = page.fields?.pageMetafields?.fields;
  const showReviewsTitle =
    (shouldUseYotpoReviews() && enableYotpoReviewsWidget) ||
    (shouldUseTrustedShopsReviews() && enableTrustedShopsReviewsWidget);

  const isFlagEnabled = useFeature(featureFlagName || '');
  const isPageEnabled = featureFlagName ? isFlagEnabled : true;

  if (!isPageEnabled) {
    return <NotFound />;
  }

  return (
    <ProductContextProvider
      value={{
        enableYotpoReviewsSummary,
        enableTrustedShopsReviewsSummary,
        product,
        shopifyProduct,
        inventoryStatusCopy,
        productVariantsCopy,
        carouselImagePosition,
        setCarouselImagePosition,
      }}
    >
      <Seo
        title={metadataFields?.pageTitle}
        description={metadataFields?.pageDescription}
        nofollow={metadataFields?.noFollow ?? true}
        noindex={metadataFields?.noIndex ?? true}
        canonical={buildCanonicalUrl(page)}
        openGraph={{
          url: buildCanonicalUrl(page),
        }}
      />
      <div
        className="md:pt-12"
        itemScope
        itemType="https://schema.org/Product"
        key={product.sys?.id}
      >
        <ProductSchema product={product as Entry<ExtendedProductFields>} />
        {shouldUseYotpoReviews() && enableYotpoReviewsWidget && (
          <ReviewsSchema
            totals={yotpoReviews}
            product={product as Entry<ExtendedProductFields>}
          />
        )}
        <ProductDetailsHeader
          shopifyProduct={shopifyProduct}
          shopifyProductAddOns={shopifyProductAddOns}
          shopifyAdditionalProducts={shopifyAdditionalProducts}
          product={product}
          additionalProducts={additionalProducts}
        />
        <div className="max-auto mx-auto px-6 md:container lg:max-w-[1200px]">
          <div className="border-t-[1px] sm:mt-8"></div>
          <div className="mb-8 flex flex-col py-8 md:flex-row md:py-12">
            <div className="md:w-7/12">
              {productDescription && (
                <RichText content={productDescription as Document} />
              )}
            </div>
            <div className="md:w-1/12" />
            <div className="flex flex-col pt-4 md:w-5/12 md:pt-16">
              {productFaq && <AccordionSection {...productFaq?.fields} />}
              {productSubFaq && (
                <ProductSubFAQ
                  entries={productSubFaq as TypeImage[] | TypeTextBlock[]}
                />
              )}
            </div>
          </div>
        </div>
        {lookInside && (
          <div className="relative">
            {lookInsideDivider && (
              <div className="absolute -top-5 z-0 h-24 w-full">
                <DecorativeDivider
                  {...(lookInsideDivider.fields as unknown as TypeDecorativeDividerFields)}
                />
              </div>
            )}
            {lookInsideLeadIn && (
              <DynamicComponent component={lookInsideLeadIn} />
            )}
            {lookInside.map((component, i) => (
              <DynamicComponent
                key={`${component.sys.id}-${i}`}
                component={component}
              />
            ))}
          </div>
        )}
        <div className="mx-auto">
          {marketingComponents &&
            marketingComponents.map((component, i) => (
              <DynamicComponent
                key={`${component.sys.id}-${i}`}
                component={component}
              />
            ))}
        </div>
        <div className="mx-auto max-w-[900px]">
          {showReviewsTitle && (
            <HeaderText
              size={HeaderSize.H1}
              text={getProductPageTranslation(
                productPageGeneralCopy,
                'Reviews',
              )}
              align="left"
              classes="border-b-[1px] border-charcoal-opacity-20 mb-4 mt-4 container mx-auto"
            />
          )}
          {shouldUseYotpoReviews() && enableYotpoReviewsWidget && (
            <YotpoReviewsWidget product={product} />
          )}
          {shouldUseTrustedShopsReviews() &&
            enableTrustedShopsReviewsWidget && (
              <div className="container mx-auto mb-24 px-6">
                <TrustedShopReviews
                  shopifyProduct={shopifyProduct}
                  widgetType="reviews"
                />
              </div>
            )}
        </div>
        {relatedProducts && (
          <div className="mx-auto">
            <CardsRow {...relatedProducts.fields} />
          </div>
        )}
        {newsletterSignup && (
          <NewsletterBlock
            {...(newsletterSignup.fields as unknown as TypeNewsletterBlockFields)}
          />
        )}
        <BackToTop />
      </div>
    </ProductContextProvider>
  );
};
